import _ from 'lodash'
import uuid from 'uuid'
import { DfmFeatures, MarkupManager } from 'fictiv-3d-viewer'

import {
    CLEAR_CAMERA_CONFIG,
    CLICK_ANNOTATION,
    CLICK_DFM_CARD,
    ESC_KEY_UP,
    COMMENT_CANCEL,
    COMMENT_HOVER,
    COMMENT_SELECT,
    CROSS_SECTION_TOOL_CLICK,
    HOVER_CROSS_SECTION_TOOL,
    HOVER_DFM_CARD,
    HOVER_HOLE_GROUP,
    HOVER_HOLE,
    HOVER_PATCH,
    HOVER_POINT,
    INSTANTIATE_VIEWER,
    MULTI_PATCH_CLICK,
    MULTI_PATCH_HOVER,
    PAN_CAMERA,
    PATCH_CLICK,
    PATCH_CTRL_CLICK,
    PATCH_RIGHT_CLICK,
    POINT_LEFT_CLICK,
    RESET_ANNOTATION_PINS,
    RESET_STATE,
    RESET_VIEW,
    ROTATE_CAMERA,
    SET_ACTIVE_DFM_FEATURES,
    SET_CROSS_SECTION_VIEW,
    SET_INTERACTION_MODE_COLOR,
    SET_MEASUREMENT_UNITS,
    SET_PDF_INSPECTION,
    SET_RAW_INSPECTION,
    SET_SELECTED_PULL_DIRECTION,
    SET_TEXTURE_THRESHOLD,
    SET_TOOL_VISIBILITY,
    SET_TRANSPARENCY,
    SET_VIEWER_INTERACTION_MODE,
    TOGGLE_DEFAULT_DFM_SELECTION,
    TOGGLE_HIDE_XSECTION_TOOLS,
    ViewerActionType,
    ZOOM_CAMERA,
} from './actions'
import {
    ANNOTATION_MODE,
    CROSS_SECTION_MODE,
    CROSS_SECTION_ANNOTATION_MODE,
    CUSTOM,
    DEFAULT_STATE,
    DfmFeaturesTypes,
    NEW_COMMENT_ID,
    POINT_TO_POINT_MODE,
    SELECT_MODE,
    TAPS_INSPECTION_MODE,
    TEXTURED_FEATURES,
} from './constants'
import { AnnotationType, PatchConfigType, ViewerStateType } from './types'

function assignState(
    state: ViewerStateType | {},
    newState: Partial<ViewerStateType>,
): ViewerStateType {
    return {
        ...DEFAULT_STATE,
        ...state,
        ...newState,
    }
}

function mergeConfig(
    state: ViewerStateType,
    config: keyof ViewerStateType,
    newConfig: Record<string, any>,
): ViewerStateType {
    const originalConfigs = state[config]

    return assignState(state, {
        [config]: {
            ...originalConfigs,
            ...newConfig,
        },
    })
}

export default function reducer(
    state = {} as ViewerStateType,
    action: ViewerActionType,
) {
    const { type: actionType } = action

    switch (actionType) {
        case RESET_STATE:
            return assignState(state, DEFAULT_STATE)
        case SET_TRANSPARENCY: {
            const { transparent } = action

            return assignState(state, {
                selectedDfmId: null,
                transparent,
            })
        }
        case SET_INTERACTION_MODE_COLOR: {
            const { color: selectedPaletteColor } = action

            return assignState(state, {
                selectedPaletteColor,
                interactionMode: SELECT_MODE,
                selectedCommentId: null,
                selectedDfmId: null,
            })
        }
        case SET_CROSS_SECTION_VIEW: {
            const { interactionMode } = state

            let updatedState = state

            const { crossSectionView } = action

            if (interactionMode === CROSS_SECTION_MODE) {
                updatedState = mergeConfig(state, 'crossSectionConfig', {
                    view: crossSectionView,
                    hoveredTool: null,
                })
            }

            return assignState(updatedState, {
                selectedCommentId: null,
                selectedDfmId: null,
                interactionMode: CROSS_SECTION_MODE,
            })
        }
        case SET_RAW_INSPECTION: {
            const { visible } = action

            return assignState(state, {
                displayRawInspection: visible,
                displayPdfInspection: false,
            })
        }
        case SET_PDF_INSPECTION: {
            const { visible } = action

            return assignState(state, {
                displayPdfInspection: visible,
                displayRawInspection: false,
            })
        }
        case SET_VIEWER_INTERACTION_MODE: {
            const {
                annotatable: actionAnnotatable,
                selectAllDfmByDefault: actionSelectAllDfmByDefault,
                mode,
            } = action

            const { interactionMode: prevInteractionMode } = state
            let interactionMode = mode

            const annotatable =
                actionAnnotatable != null
                    ? actionAnnotatable
                    : state.annotatable

            const selectAllDfmByDefault =
                actionSelectAllDfmByDefault != null
                    ? actionSelectAllDfmByDefault
                    : state.selectAllDfmByDefault

            const { crossSectionConfig } = state

            if (crossSectionConfig) {
                if (mode === CROSS_SECTION_MODE) {
                    crossSectionConfig.enabled = true
                    crossSectionConfig.toolsHidden = !annotatable
                } else if (
                    prevInteractionMode === CROSS_SECTION_MODE &&
                    mode === ANNOTATION_MODE
                ) {
                    interactionMode = CROSS_SECTION_ANNOTATION_MODE
                    crossSectionConfig.enabled = true
                    crossSectionConfig.toolsHidden = true
                } else {
                    crossSectionConfig.enabled = false
                    crossSectionConfig.toolsHidden = true
                }
                crossSectionConfig.mode = interactionMode
            }

            const selectedPatchConfigs =
                mode === POINT_TO_POINT_MODE ? [] : state.selectedPatchConfigs

            const selectedPoints =
                mode === SELECT_MODE ? [] : state.selectedPoints

            return assignState(state, {
                interactionMode,
                annotatable,
                crossSectionConfig,
                selectAllDfmByDefault,
                selectedCommentId: null,
                selectedDfmId: null,
                selectedTapsPatchHash: null,
                hoveredTapsPatchHash: null,
                selectedPatchConfigs,
                selectedPoints,
                forceHideCrossSectionTools: false,
            })
        }
        case SET_ACTIVE_DFM_FEATURES: {
            const activeDfmFeatures = new Set(state.activeDfmFeatures)

            const { features, visible } = action
            // Only allow one textured feature to be active at once
            // Grab the first one in the new set of active features
            const newTexturedFeature = features?.find(feature =>
                TEXTURED_FEATURES.includes(feature),
            )

            _.each(features, feature => {
                if (visible) {
                    activeDfmFeatures.add(feature)
                } else {
                    activeDfmFeatures.delete(feature)
                }
            })

            // If we are not trying to set a texture, keep all features
            // Otherwise, keep all non textured features and the new texturedFeature
            const finalActiveDfmFeatures = Array.from(activeDfmFeatures).filter(
                feature =>
                    !newTexturedFeature ||
                    !TEXTURED_FEATURES.includes(feature) ||
                    feature === newTexturedFeature,
            )

            return assignState(state, {
                selectedDfmId: null,
                activeDfmFeatures: finalActiveDfmFeatures,
            })
        }
        case SET_TEXTURE_THRESHOLD: {
            const { threshold: textureThreshold } = action

            return assignState(state, {
                selectedDfmId: null,
                textureThreshold,
            })
        }
        case SET_SELECTED_PULL_DIRECTION: {
            const { selectedPullDirection } = action

            return assignState(state, {
                selectedDfmId: null,
                selectedPullDirection,
            })
        }
        case SET_MEASUREMENT_UNITS: {
            const { units } = action

            return assignState(state, {
                units: units,
            })
        }
        case HOVER_PATCH: {
            const {
                activeDfmFeatures,
                interactionMode,
                selectedPullDirection,
            } = state

            const { patchHash } = action

            if (interactionMode === CROSS_SECTION_MODE) {
                return mergeConfig(state, 'crossSectionConfig', {
                    hoveredTool: null,
                })
            }

            if (interactionMode === SELECT_MODE) {
                const isDraftAnalysisSelected = activeDfmFeatures.includes(
                    DfmFeatures.DRAFT_ANALYSIS,
                )

                const isCustomPullDirection =
                    selectedPullDirection === CUSTOM ||
                    Array.isArray(selectedPullDirection)

                if (isDraftAnalysisSelected && isCustomPullDirection) {
                    return assignState(state, {
                        hoveredHighlightPatchHash: patchHash,
                    })
                }

                return assignState(state, {
                    hoveredPatch: patchHash,
                })
            }

            if (interactionMode === TAPS_INSPECTION_MODE) {
                return assignState(state, {
                    hoveredTapsPatchHash: patchHash,
                })
            }

            return state
        }
        case HOVER_POINT: {
            const { interactionMode } = state
            const { pointIndex: index, pointType: type } = action

            if (interactionMode === POINT_TO_POINT_MODE) {
                const hoveredPoint =
                    typeof index === 'number' && type ? { index, type } : null

                return assignState(state, {
                    hoveredPoint,
                })
            }

            return state
        }
        case POINT_LEFT_CLICK: {
            const { interactionMode, selectedPoints } = state
            const { pointIndex: index, pointType: type } = action

            if (interactionMode !== POINT_TO_POINT_MODE) {
                return state
            }

            /*
                Eventually, when allowing for multiple point to point lines, and we are selecting an
                already selected point, we will want to deselect its pair. i.e. given:
                1) points A B C, click A -> remove A B
                2) points A B C, click B -> remove A B
                3) points A B C, click C -> remove C
            */
            let _selectedPoints = []

            if (
                selectedPoints.find(
                    point => point.index === index && point.type === type,
                )
            ) {
                _selectedPoints = selectedPoints.filter(
                    point => point.index === index && point.type === type,
                )
            } else if (selectedPoints.length === 2) {
                _selectedPoints = [{ index, type }]
            } else {
                _selectedPoints = [...selectedPoints]
                _selectedPoints.push({ index, type })
            }

            return assignState(state, {
                selectedPoints: _selectedPoints,
                hoveredPoint: null,
            })
        }
        case MULTI_PATCH_HOVER: {
            const { interactionMode } = state
            const { patchHashes } = action

            if (interactionMode === SELECT_MODE) {
                return assignState(state, {
                    hoveredPatches: patchHashes,
                })
            }

            return state
        }
        case MULTI_PATCH_CLICK: {
            const { interactionMode, selectedPaletteColor } = state
            const { patchHashes } = action

            if (interactionMode === SELECT_MODE) {
                const selectedPatchConfigs = [...state.selectedPatchConfigs]

                patchHashes.forEach(_hashcode => {
                    if (
                        !selectedPatchConfigs.find(
                            ({ hashcode }) => hashcode === _hashcode,
                        )
                    ) {
                        selectedPatchConfigs.push({
                            hashcode: _hashcode,
                            color: selectedPaletteColor,
                        })
                    }
                })

                return assignState(state, {
                    selectedDfmId: null,
                    selectedPatchConfigs,
                    selectedCommentId: NEW_COMMENT_ID,
                })
            }

            return state
        }
        case RESET_ANNOTATION_PINS: {
            return assignState(state, {
                annotations: {},
            })
        }
        case HOVER_CROSS_SECTION_TOOL: {
            const { interactionMode } = state
            const { toolName: hoveredTool } = action

            if (interactionMode === CROSS_SECTION_MODE) {
                return assignState(state, {
                    ...mergeConfig(state, 'crossSectionConfig', {
                        view: null,
                        hoveredTool,
                    }),
                    selectedCommentId: NEW_COMMENT_ID,
                })
            }

            return state
        }
        case PATCH_CLICK: {
            const {
                activeDfmFeatures,
                interactionMode,
                selectedPaletteColor,
                selectedPullDirection,
                selectedPullDirectionPatchHash,
                viewer,
            } = state

            const {
                patchClickEvent: {
                    patchHash: _hashcode,
                    pointPartCoordinates,
                    face,
                },
            } = action

            if (interactionMode === SELECT_MODE) {
                const isDraftAnalysisSelected = activeDfmFeatures.includes(
                    DfmFeatures.DRAFT_ANALYSIS,
                )
                const isCustomPullDirection =
                    selectedPullDirection === CUSTOM ||
                    Array.isArray(selectedPullDirection)

                if (isDraftAnalysisSelected && isCustomPullDirection) {
                    let direction = viewer.getPullDirectionFromPatchHashes({
                        patchHashes: [_hashcode],
                    })

                    if (!direction) {
                        return
                    }

                    if (
                        selectedPullDirectionPatchHash === _hashcode &&
                        JSON.stringify(direction) ===
                            JSON.stringify(selectedPullDirection)
                    ) {
                        direction = direction.map((axis: number) => axis * -1)
                    }

                    return assignState(state, {
                        selectedPullDirection: direction,
                        selectedPullDirectionPatchHash: _hashcode,
                    })
                }

                const selectedPatchConfigs = [...state.selectedPatchConfigs]

                const idx = selectedPatchConfigs.findIndex(
                    ({ hashcode }) => hashcode === _hashcode,
                )

                if (idx === -1) {
                    selectedPatchConfigs.push({
                        hashcode: _hashcode,
                        color: selectedPaletteColor,
                    })
                } else {
                    selectedPatchConfigs.splice(idx, 1)
                }

                return assignState(state, {
                    selectedDfmId: null,
                    selectedPatchConfigs,
                    selectedCommentId: NEW_COMMENT_ID,
                })
            }

            if (interactionMode === ANNOTATION_MODE) {
                const position = MarkupManager.getFaceNormalOffsetPosition(
                    pointPartCoordinates,
                    face,
                    true,
                )
                const annotationId = uuid.v4()
                const annotationConfig = {
                    position,
                    metadata: { id: annotationId },
                }

                return assignState(state, {
                    selectedDfmId: null,
                    annotations: {
                        ...state.annotations,
                        [annotationId]: annotationConfig,
                    },
                    selectedCommentId: NEW_COMMENT_ID,
                })
            }

            if (interactionMode === TAPS_INSPECTION_MODE) {
                return assignState(state, {
                    selectedTapsPatchHash: _hashcode,
                })
            }

            return state
        }
        case PATCH_CTRL_CLICK: {
            const { interactionMode, viewer } = state
            const {
                patchClickEvent: {
                    face: { normal: faceNormal },
                },
            } = action

            if (
                interactionMode === SELECT_MODE ||
                interactionMode === POINT_TO_POINT_MODE ||
                interactionMode === ANNOTATION_MODE
            ) {
                viewer.tweenCameraToLookDown(faceNormal)
            }

            return state
        }
        case PATCH_RIGHT_CLICK: {
            const { interactionMode } = state
            const {
                patchClickEvent: { pointPartCoordinates },
            } = action

            if (interactionMode === POINT_TO_POINT_MODE) {
                const customP2PPoints = [...state.customP2PPoints]

                customP2PPoints.push(pointPartCoordinates.toArray())

                return assignState(state, {
                    selectedDfmId: null,
                    customP2PPoints,
                })
            }

            return state
        }
        case CLICK_ANNOTATION: {
            const annotationConfigs = { ...state.annotations }
            const {
                annotation: { metadata: { id: annotationId = null } = {} } = {},
            } = action

            delete annotationConfigs[annotationId]
            return assignState(state, {
                selectedDfmId: null,
                annotations: annotationConfigs,
            })
        }
        case CROSS_SECTION_TOOL_CLICK: {
            const { interactionMode } = state
            const { pointCoordinates, normal } = action

            if (interactionMode === CROSS_SECTION_ANNOTATION_MODE) {
                const annotationId = uuid.v4()

                const position = MarkupManager.getNormalOffsetPosition(
                    pointCoordinates,
                    normal,
                    true,
                )

                const annotationConfig = {
                    position,
                    metadata: { id: annotationId },
                }

                return assignState(state, {
                    selectedDfmId: null,
                    annotations: {
                        ...state.annotations,
                        [annotationId]: annotationConfig,
                    },
                    selectedCommentId: NEW_COMMENT_ID,
                })
            }

            return state
        }
        case INSTANTIATE_VIEWER: {
            const { viewer } = action
            const defaultCameraConfig = viewer.getDefaultCameraProps()

            const mappedCameraConfig = {
                zoom: defaultCameraConfig.zoom,
                position: defaultCameraConfig.positionArray,
                target: defaultCameraConfig.targetArray,
                up: defaultCameraConfig.upArray,
            }

            return assignState(state, {
                viewer,
                defaultCameraConfig: mappedCameraConfig,
            })
        }
        case HOVER_HOLE: {
            const { holeId } = action

            return assignState(state, {
                hoveredHoleId: holeId,
            })
        }
        case HOVER_HOLE_GROUP: {
            const { groupId } = action

            return assignState(state, {
                hoveredHoleGroupId: groupId,
            })
        }
        case HOVER_DFM_CARD: {
            const { dfmId: hoveredDfmId } = action

            return assignState(state, {
                hoveredDfmId,
            })
        }
        case COMMENT_HOVER: {
            const { commentId: hoveredCommentId } = action

            return assignState(state, {
                hoveredCommentId,
            })
        }
        case COMMENT_SELECT: {
            const { commentId: selectedCommentId } = action

            return assignState(state, {
                selectedCommentId,
            })
        }
        case COMMENT_CANCEL: {
            return assignState(state, {
                selectedCommentId: null,
                selectedPatchConfigs: [],
                annotations: {},
            })
        }
        case CLICK_DFM_CARD: {
            const {
                crossSectionConfig: prevCrossSectionConfig,
                interactionMode: prevInteractionMode,
            } = state
            const { dfmSnapshot, dfmId: selectedDfmId } = action

            const {
                dfm: dfmFeatureConfigs = [],
                part: { transparent = false, patches = [] } = {},
                camera: cameraConfig = null,
                rotation: partRotation = null,
                annotations = [],
                crossSection,
                pointToPoint,
            } = dfmSnapshot || {}

            const textureThreshold = dfmFeatureConfigs.find(({ feature }) => {
                if (!feature) {
                    return
                }

                return TEXTURED_FEATURES.includes(feature)
            })?.threshold

            // Filter out __typeName
            const selectedPatchConfigs = patches.reduce(
                (configs: PatchConfigType[], { hashcode, color }) => {
                    configs.push({ hashcode, color })
                    return configs
                },
                [],
            )

            const customP2PPoints = pointToPoint?.customPoints || []

            // This makes it so that measurements that involve custom points
            // are not one render behind
            state.viewer?.setCustomMeasurementPoints({
                points: customP2PPoints,
            })

            const interactionMode = crossSection
                ? CROSS_SECTION_MODE
                : prevInteractionMode

            const crossSectionConfig = {
                ...prevCrossSectionConfig,
                mode: interactionMode,
            }

            return assignState(state, {
                interactionMode,
                crossSectionConfig,
                selectedDfmId,
                activeDfmFeatures: _.reduce(
                    dfmFeatureConfigs,
                    (result: (string | null)[], { feature }) => {
                        return feature === DfmFeatures.THINWALL
                            ? result
                            : [...result, feature]
                    },
                    [],
                ).filter(feature => feature) as DfmFeaturesTypes[],
                textureThreshold,
                transparent,
                selectedPatchConfigs,
                cameraConfig,
                partRotation,
                selectedPoints: pointToPoint?.selectedPoints || [],
                customP2PPoints,
                annotations: _.reduce(
                    annotations,
                    (
                        result: {
                            [annotationId: string]: AnnotationType
                        },
                        { position },
                    ) => {
                        const annotationId = uuid.v4()

                        result[annotationId] = {
                            position,
                            metadata: { id: annotationId },
                        }
                        return result
                    },
                    {},
                ),
            })
        }
        case ROTATE_CAMERA:
        case PAN_CAMERA:
        case ZOOM_CAMERA: {
            const { newCameraConfig } = action

            return {
                ...state,
                cameraConfig: newCameraConfig,
            }
        }
        case RESET_VIEW:
            return assignState(state, {
                selectedDfmId: null,
                interactionMode: state.annotatable
                    ? state.interactionMode
                    : SELECT_MODE,
                crossSectionConfig: state.annotatable
                    ? state.crossSectionConfig
                    : undefined,
                activeDfmFeatures: state.annotatable
                    ? state.activeDfmFeatures
                    : [],
                cameraConfig: state.defaultCameraConfig,
            })
        case CLEAR_CAMERA_CONFIG: {
            return {
                ...state,
                cameraConfig: null,
                defaultCameraConfig: null,
            }
        }
        case ESC_KEY_UP:
            return assignState(state, {
                partRotation: null,
                selectedPatchConfigs: [],
                annotations: {},
                selectedPoints: [],
                selectedCommentId: null,
                selectedDfmId: null,
                forceHideCrossSectionTools: false,
            })
        case TOGGLE_DEFAULT_DFM_SELECTION:
            return assignState(state, {
                selectAllDfmByDefault: !state.selectAllDfmByDefault,
            })
        case TOGGLE_HIDE_XSECTION_TOOLS: {
            const {
                crossSectionConfig,
                forceHideCrossSectionTools: oldForceHideCrossSectionTools,
            } = state

            const forceHideCrossSectionTools = !oldForceHideCrossSectionTools

            const newCrossSectionConfig = crossSectionConfig && {
                ...crossSectionConfig,
                toolsHidden: forceHideCrossSectionTools,
                enabled: !forceHideCrossSectionTools,
            }

            return assignState(state, {
                crossSectionConfig: newCrossSectionConfig,
                forceHideCrossSectionTools,
            })
        }
        case SET_TOOL_VISIBILITY:
            return assignState(state, {
                toolVisibility: action.toolVisibility,
            })
        default:
            return state
    }
}
